import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getPost, deletePost, addComment } from "../lib/api";
import { format, parseISO } from "date-fns";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateCommentSchema } from "../lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";

export default function Post() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: post, isLoading } = useQuery({
    queryKey: ["post", postId],
    queryFn: () => {
      if (!postId) throw new Error("Post ID is required");
      return getPost(postId);
    },
    enabled: !!postId,
  });

  const deleteMutation = useMutation({
    mutationFn: deletePost,
    onSuccess: () => {
      navigate("/app/posts");
    },
  });

  const handleDelete = () => {
    if (
      postId &&
      window.confirm("Are you sure you want to delete this post?")
    ) {
      deleteMutation.mutate(postId);
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(CreateCommentSchema),
    defaultValues: {
      body: "",
    },
  });

  const commentMutation = useMutation({
    mutationFn: (data: z.infer<typeof CreateCommentSchema>) => {
      if (!postId) throw new Error("Post ID is required");
      return addComment(postId, data);
    },
    onSuccess: () => {
      if (!postId) return;
      queryClient.invalidateQueries({ queryKey: ["posts", postId] });
      reset();
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!post) {
    return (
      <div>
        <div>Error loading post</div>
        <Link to="/app/posts">Back to posts</Link>
      </div>
    );
  }

  const components: Components = {
    h1: ({ ...props }) => <h1 className="my-4" {...props} />,
    h2: ({ ...props }) => <h2 className="my-3" {...props} />,
    h3: ({ ...props }) => <h3 className="my-2" {...props} />,
    p: ({ ...props }) => <p className="my-2" {...props} />,
    em: ({ ...props }) => (
      <em
        className="italic"
        style={{ color: "var(--color-plasma-sage)" }}
        {...props}
      />
    ),
    strong: ({ ...props }) => (
      <strong
        className="font-bold"
        style={{ color: "var(--color-quantum-iris)" }}
        {...props}
      />
    ),
    blockquote: ({ ...props }) => (
      <blockquote
        className="pl-4 my-4"
        style={{ borderLeft: "4px solid var(--color-quantum-iris)" }}
        {...props}
      />
    ),
    code: ({ node, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || "");
      const isInline = node?.position?.start.line === node?.position?.end.line;

      if (!isInline) {
        return (
          <pre
            className="p-4 rounded overflow-x-auto"
            style={{ background: "var(--color-stellar-black)" }}
          >
            <code
              className={match ? `language-${match[1]}` : ""}
              style={{ color: "var(--color-aurora-white)" }}
              {...props}
            >
              {children}
            </code>
          </pre>
        );
      }

      return (
        <code
          className="rounded px-1"
          style={{
            background: "var(--color-stellar-black)",
            color: "var(--color-aurora-white)",
          }}
          {...props}
        >
          {children}
        </code>
      );
    },
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8 flex items-center justify-between">
        <Link to="/app/posts" className="text-indigo-600 hover:text-indigo-800">
          ← Back to posts
        </Link>
        {post?.isAuthor && (
          <div className="flex gap-4">
            <Link
              to={`/app/posts/${postId}/edit`}
              className="text-indigo-600 hover:text-indigo-800"
            >
              Edit post
            </Link>
            <button
              onClick={handleDelete}
              className="text-red-600 hover:text-red-800"
            >
              Delete post
            </button>
          </div>
        )}
      </div>

      <article className="prose prose-lg max-w-none card">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {post.body}
        </ReactMarkdown>
      </article>

      <div className="mt-12">
        <h2 className="text-2xl font-bold">Comments</h2>
        <div className="mt-4 space-y-6">
          {post.comments?.map((comment, index) => (
            <div key={index} className="card">
              <div className="flex items-center justify-between">
                <span style={{ color: "var(--color-quantum-iris)" }}>
                  {comment.authorEmail}
                </span>
                <time className="text-sm" dateTime={comment.createdAt}>
                  {comment.createdAt
                    ? format(parseISO(comment.createdAt), "MMM d, yyyy")
                    : ""}
                </time>
              </div>
              <p className="mt-2">{comment.body}</p>
            </div>
          ))}
        </div>

        <form
          onSubmit={handleSubmit((data) => commentMutation.mutate(data))}
          className="mt-8 space-y-4"
        >
          <div>
            <label htmlFor="body" className="label">
              Comment
            </label>
            <textarea
              id="body"
              rows={4}
              {...register("body")}
              className="input mt-1"
            />
            {errors.body && (
              <p className="mt-1 text-sm text-red-600">
                {errors.body.message as string}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting || commentMutation.isPending}
            className="btn btn-primary"
          >
            Add Comment
          </button>
        </form>
      </div>
    </div>
  );
}
